<template>
  <div class="releases-page">
    <nav-component></nav-component>

    <section class="releases-page__hero">
      <div class="rt-inner releases-page__hero-inner">
        <span class="rt-eyebrow releases-page__eyebrow">RELEASE NOTES</span>
        <h1 class="releases-page__title">リリース</h1>
        <p class="releases-page__lead">
          公開された各システムの更新情報を、新しいものから順に掲載しています。
        </p>
      </div>
    </section>

    <section class="releases-page__body">
      <div class="rt-inner">
        <div class="releases-page__summary">
          <span>{{ allRecords }}件のリリース情報</span>
          <span v-if="allPages > 1">{{ page + 1 }} / {{ allPages }}ページ</span>
        </div>

        <p v-if="errorMessage" class="releases-page__alert">{{ errorMessage }}</p>
        <p v-if="isLoading" class="releases-page__loading">読み込み中です。</p>

        <div v-if="!isLoading && releaseList.length > 0" class="releases-page__list">
          <article
            v-for="release in releaseList"
            :key="release.id"
            class="releases-page__item"
          >
            <div class="releases-page__item-head">
              <div class="releases-page__meta">
                <time class="releases-page__date">{{ formatReleaseDate(release.productionReleaseDate) }}</time>
                <span class="releases-page__system">{{ releaseSystemLabel(release.system) }}</span>
                <span
                  v-for="subsystem in releaseSubsystemBadgeList(release)"
                  :key="`${release.id}-${subsystem}`"
                  class="releases-page__subsystem"
                  :class="`releases-page__subsystem--${subsystem}`"
                >{{ releaseSubsystemLabel(subsystem) }}</span>
              </div>
              <div class="releases-page__version">{{ release.version || '-' }}</div>
            </div>
            <div class="releases-page__note" v-html="releaseContentHtml(release.releaseNote)"></div>
          </article>
        </div>
        <p v-else-if="!isLoading" class="releases-page__empty">リリース情報はありません</p>

        <nav v-if="allPages > 1" class="releases-page__pager" aria-label="リリース一覧ページ">
          <button
            type="button"
            class="releases-page__pager-btn"
            aria-label="最初のページへ"
            title="最初のページへ"
            :disabled="isLoading || page === 0"
            @click="firstPage"
          >
            <i class="fas fa-fast-backward" aria-hidden="true"></i>
          </button>
          <button
            type="button"
            class="releases-page__pager-btn"
            aria-label="前のページへ"
            title="前のページへ"
            :disabled="isLoading || page === 0"
            @click="prevPage"
          >
            <i class="fas fa-chevron-left" aria-hidden="true"></i>
          </button>
          <span class="releases-page__pager-current">{{ page + 1 }} / {{ allPages }}</span>
          <button
            type="button"
            class="releases-page__pager-btn"
            aria-label="次のページへ"
            title="次のページへ"
            :disabled="isLoading || page >= allPages - 1"
            @click="nextPage"
          >
            <i class="fas fa-chevron-right" aria-hidden="true"></i>
          </button>
          <button
            type="button"
            class="releases-page__pager-btn"
            aria-label="最後のページへ"
            title="最後のページへ"
            :disabled="isLoading || page >= allPages - 1"
            @click="lastPage"
          >
            <i class="fas fa-fast-forward" aria-hidden="true"></i>
          </button>
        </nav>
      </div>
    </section>

    <site-footer></site-footer>
  </div>
</template>

<script>
import { defineComponent, onMounted, ref } from 'vue'
import axios from 'axios'
import navComponent from '../components/Nav.vue'
import SiteFooter from '../components/SiteFooter.vue'
import { releaseListRequest } from '../lib/monstrsApi'
import {
  formatReleaseDate,
  releaseSubsystemBadgeList,
  releaseContentHtml,
  releaseSubsystemLabel,
  releaseSystemLabel
} from '../lib/releases'

const PER_PAGE = 20
const API_BASE = process.env.VUE_APP_MONSTRS_API

export default defineComponent({
  name: 'ReleasesView',
  components: { navComponent, SiteFooter },
  setup () {
    const page = ref(0)
    const releaseList = ref([])
    const allRecords = ref(0)
    const allPages = ref(0)
    const isLoading = ref(false)
    const errorMessage = ref('')

    const loadReleases = (nextPage = page.value) => {
      page.value = nextPage
      isLoading.value = true
      errorMessage.value = ''
      const req = releaseListRequest(API_BASE, page.value, PER_PAGE)
      axios.get(req.url, { params: req.params }).then(response => {
        const data = response.data || {}
        releaseList.value = data.list || []
        page.value = typeof data.page === 'number' ? data.page : page.value
        allRecords.value = Number(data.allRecords || 0)
        allPages.value = Number(data.allPages || 0)
      }).catch(() => {
        releaseList.value = []
        allRecords.value = 0
        allPages.value = 0
        errorMessage.value = 'リリース情報の読み込みに失敗しました。時間をおいて再度お試しください。'
      }).finally(() => {
        isLoading.value = false
      })
    }

    const movePage = (nextPage) => {
      loadReleases(nextPage)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
    const firstPage = () => movePage(0)
    const prevPage = () => movePage(Math.max(page.value - 1, 0))
    const nextPage = () => movePage(Math.min(page.value + 1, allPages.value - 1))
    const lastPage = () => movePage(allPages.value - 1)

    onMounted(() => {
      loadReleases(0)
    })

    return {
      allPages,
      allRecords,
      errorMessage,
      formatReleaseDate,
      firstPage,
      isLoading,
      lastPage,
      loadReleases,
      nextPage,
      page,
      prevPage,
      releaseContentHtml,
      releaseSubsystemBadgeList,
      releaseList,
      releaseSubsystemLabel,
      releaseSystemLabel
    }
  }
})
</script>

<style scoped>
.releases-page {
  background: var(--base);
}

.releases-page__hero {
  position: relative;
  overflow: hidden;
  padding: 116px 24px 24px;
  background:
    radial-gradient(circle at 88% 14%, rgba(0, 151, 224, 0.16), transparent 42%),
    radial-gradient(circle at 18% 92%, rgba(238, 134, 168, 0.14), transparent 40%);
}

.releases-page__hero-inner {
  text-align: left;
}

.releases-page__eyebrow {
  color: var(--brand-blue);
  background: var(--blue-bg);
}

.releases-page__title {
  font-size: clamp(26px, 5vw, 34px);
  font-weight: 900;
  color: var(--ink);
}

.releases-page__lead {
  font-size: 14px;
  line-height: 1.9;
  color: var(--ink-soft);
  margin-top: 10px;
  max-width: 520px;
}

.releases-page__body {
  padding: 24px 24px 64px;
}

.releases-page__summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  color: var(--ink-mute);
  font-size: 12px;
  margin-bottom: 12px;
}

.releases-page__list {
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--radius);
  overflow: hidden;
}

.releases-page__alert,
.releases-page__loading {
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--radius);
  padding: 14px 18px;
  text-align: left;
  font-size: 13px;
  margin-bottom: 12px;
}

.releases-page__alert {
  color: var(--brand-red);
  background: #fff8f6;
}

.releases-page__loading {
  color: var(--ink-soft);
}

.releases-page__item {
  padding: 20px 22px;
  border-bottom: 1px solid var(--line);
  text-align: left;
}

.releases-page__item:last-child {
  border-bottom: none;
}

.releases-page__item-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 10px;
}

.releases-page__meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  min-width: 0;
}

.releases-page__date {
  font-family: var(--font-lat);
  font-size: 12px;
  color: var(--ink-mute);
  white-space: nowrap;
}

.releases-page__system,
.releases-page__subsystem {
  display: inline-flex;
  align-items: center;
  min-height: 24px;
  border-radius: var(--radius-pill);
  padding: 3px 10px;
  font-family: var(--font-lat);
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
}

.releases-page__system {
  color: var(--brand-blue);
  background: var(--blue-bg);
}

.releases-page__subsystem {
  color: var(--ink-soft);
  background: var(--surface-soft);
}

.releases-page__subsystem--blue {
  color: var(--brand-blue);
  background: var(--blue-bg);
}

.releases-page__subsystem--red {
  color: var(--brand-red);
  background: #fff0ed;
}

.releases-page__subsystem--green {
  color: #66930e;
  background: var(--green-bg);
}

.releases-page__subsystem--pink {
  color: var(--brand-pink);
  background: var(--pink-bg);
}

.releases-page__version {
  flex: 0 0 auto;
  font-family: var(--font-lat);
  font-size: 14px;
  font-weight: 700;
  color: var(--ink);
  white-space: nowrap;
}

.releases-page__note {
  color: var(--ink-soft);
  font-size: 13.5px;
  line-height: 1.85;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.releases-page__note :deep(p) {
  margin-bottom: 8px;
}

.releases-page__note :deep(ul),
.releases-page__note :deep(ol) {
  margin: 0 0 8px;
  padding-left: 1.3rem;
}

.releases-page__note :deep(li) {
  margin: 2px 0;
}

.releases-page__note :deep(a) {
  color: var(--brand-blue);
}

.releases-page__note :deep(pre) {
  background: var(--surface-soft);
  border: 1px solid var(--line);
  border-radius: 8px;
  padding: 12px;
  white-space: pre-wrap;
}

.releases-page__empty {
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--radius);
  padding: 30px;
  text-align: center;
  color: var(--ink-mute);
  font-size: 14px;
}

.releases-page__pager {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 20px;
}

.releases-page__pager-btn {
  width: 38px;
  height: 38px;
  border: 1px solid var(--line);
  border-radius: 50%;
  background: var(--surface);
  color: var(--brand-blue);
  cursor: pointer;
  transition: background 0.15s, transform 0.15s, opacity 0.15s;
}

.releases-page__pager-btn:hover:not(:disabled) {
  background: var(--blue-bg);
  transform: translateY(-1px);
}

.releases-page__pager-btn:disabled {
  color: var(--ink-mute);
  cursor: default;
  opacity: 0.45;
}

.releases-page__pager-current {
  min-width: 74px;
  text-align: center;
  font-family: var(--font-lat);
  font-size: 13px;
  font-weight: 700;
  color: var(--ink-soft);
}

@media (max-width: 768px) {
  .releases-page__body {
    padding: 20px 18px 52px;
  }

  .releases-page__summary,
  .releases-page__item-head {
    align-items: flex-start;
    flex-direction: column;
  }

  .releases-page__item {
    padding: 18px 16px;
  }

  .releases-page__version {
    white-space: normal;
  }
}
</style>
